/**
 * Manual del Supervisor.
 * Verified behavior (V17+): el Supervisor PUEDE someter recibos en Gastos
 * (POST /receipts permite `supervisor`), pero NO aprueba ni codifica gastos.
 * Su trabajo principal en Nómina es CREAR y SOMETER tarjetas de tiempo
 * (daily sheets). No ve la validación AMGtime (solo Ingeniero/Gerente/Revisión/Admin).
 * Contenido derivado del código real: NominaHomePage, ProjectSheetsPage,
 * TarjetaPage, SearchableSelect, nominaController (caps 8h/40h) y shared/constants.
 */
export default {
  roleKey: 'supervisor',
  roleLabel: 'Supervisor(a)',
  icon: '⏱️',
  subtitle: 'Módulo de Nómina — crear y someter tarjetas de tiempo. También puede someter gastos.',
  sections: [
    {
      id: 'someter-gasto',
      group: 'Gastos',
      title: 'Cómo someter un gasto',
      blocks: [
        { type: 'paragraph', text: 'Además de Nómina, usted puede someter recibos de gastos, igual que un Chofer. Usted no aprueba ni codifica gastos: solo los somete.' },
        { type: 'steps', items: [
          'En el menú, abra "Gastos" → "Mis Recibos" y pulse "+ Nuevo Gasto".',
          'Suba la foto o el PDF del recibo. La IA autocompleta el proveedor, la fecha, el subtotal y las partidas; revise que coincidan.',
          'Complete los datos del recibo (Proveedor, Fecha, Método de Pago) y el "Subtotal del Recibo".',
          'En cada partida elija el Proyecto (el Supervisor se asigna solo), escriba la Descripción y el Monto.',
          'Verifique que los montos de las partidas sumen el subtotal y pulse "Enviar Reporte".',
        ] },
        { type: 'note', variant: 'info', text: 'Puede guardar el recibo como borrador y terminarlo después. El recibo enviado va al Ingeniero(a) del proyecto para su revisión.' },
      ],
    },
    {
      id: 'nomina-principal',
      group: 'Nómina',
      title: 'Pantalla principal de Nómina',
      blocks: [
        { type: 'paragraph', text: 'En el menú abra "Horas" para entrar a Nómina. Verá una tarjeta por cada uno de sus proyectos activos.' },
        { type: 'steps', items: [
          'Cada proyecto muestra su código, nombre y si es de 2 o 3 niveles de aprobación.',
          'Pulse el proyecto para expandirlo y ver sus tarjetas agrupadas por estado.',
          'Use el botón "Nueva" (o "Ver todos los detalles") para entrar al proyecto y trabajar sus tarjetas.',
        ] },
        { type: 'subheading', text: 'Estados de una tarjeta' },
        { type: 'list', items: [
          'Borrador — creada pero todavía no sometida (usted puede editarla).',
          'Pendiente Ingeniero / Pend. Gerente / Rev. Final — sometida y en proceso de aprobación.',
          'Devuelto — le fue regresada para corregir (usted puede editarla y reenviarla).',
          'Aprobada — aprobada por la cadena completa.',
        ] },
        { type: 'note', variant: 'info', text: 'La mini-cadena junto a cada tarjeta (Supervisor(a) → Ingeniero(a) → Gerente → Rev. Final) muestra el avance: ✓ aprobado, color ámbar el nivel actual, ↩ a quién se devolvió. Solo las tarjetas en Borrador o Devuelto se abren para editar; las demás se abren en modo solo lectura.' },
      ],
    },
    {
      id: 'crear-tarjeta',
      group: 'Nómina',
      title: 'Crear una tarjeta de tiempo nueva',
      blocks: [
        { type: 'steps', items: [
          'En Nómina, pulse "Nueva" en el proyecto (o entre al proyecto y use "Nueva Tarjeta").',
          'Elija la fecha de la tarjeta. Por defecto se sugiere el día siguiente a la última tarjeta; no se permiten fechas futuras.',
          'Pulse "Nueva Tarjeta". Se abre la tarjeta lista para registrar las horas.',
        ] },
        { type: 'note', variant: 'info', text: 'Hay una tarjeta por día y proyecto. Si ya tiene un borrador para esa fecha, el sistema lo abre para que continúe; si ya la sometió (o está en revisión o aprobada), le avisará que ya existe una tarjeta para esa fecha.' },
        { type: 'note', variant: 'warning', text: 'Si aparece "Faltan tarjetas para…", hay días laborables sin tarjeta entre la última y la fecha elegida — créelas para no dejar huecos. Si aparece que el día es feriado o fin de semana, confirme que de verdad desea registrar horas ese día.' },
      ],
    },
    {
      id: 'registrar-horas',
      group: 'Nómina',
      title: 'Registrar horas por trabajador',
      blocks: [
        { type: 'subheading', text: 'Agregar trabajadores y tareas' },
        { type: 'steps', items: [
          'En "Buscar empleado…", escriba el nombre y selecciónelo para agregar su tarjeta.',
          'En cada trabajador, use el buscador "Buscar tarea…" para elegir la tarea (código de costo). En algunos proyectos seleccionará una "Cuenta GL" en lugar de una tarea.',
          'Escriba las horas en la columna "R" (regulares) y, si aplica, en "E" (horas extra).',
          'Use "Agregar tarea" para repartir las horas de un trabajador entre varias tareas.',
          'Marque cada trabajador como 🏗️ Presencial o 🏠 Remoto según corresponda.',
        ] },
        { type: 'note', variant: 'info', text: 'Cada tarjeta de trabajador muestra indicadores de horas acumuladas — "D: x/8h" (día) y "S: x/40h" (semana) — incluyendo lo registrado en otros proyectos. Si ve "⚠ Otros", el trabajador ya tiene horas en otro proyecto ese día.' },
        { type: 'subheading', text: 'Herramientas por trabajador' },
        { type: 'list', items: [
          'Copiar actividades: copie las tareas de un trabajador a otro para no repetirlas.',
          'Marcar ausencia: convierta a un trabajador en ausencia no remunerada (Clase, Ausencia o Personal).',
          'Notas: puede añadir notas por trabajador y notas generales de la tarjeta.',
        ] },
        { type: 'note', variant: 'info', text: 'La sección "Ausencias no remuneradas" permite registrar a quienes no trabajaron ese día (Clase / Ausencia / Personal) con "Marcar Ausencia".' },
      ],
    },
    {
      id: 'regla-8-horas',
      group: 'Nómina',
      title: 'Regla de las 8 horas regulares',
      blocks: [
        { type: 'paragraph', text: 'Un trabajador no puede tener más de 8 horas REGULARES en el día (sumando todos los proyectos), ni más de 40 horas regulares en la semana. Las horas por encima de eso van en la columna "E" (horas extra).' },
        { type: 'note', variant: 'warning', text: 'Si un trabajador pasa de 8h regulares, aparece "Excede 8h regulares por X h. Mueva el exceso a Horas Extra (E)". Pase esas horas a la columna "E" antes de someter.' },
        { type: 'note', variant: 'warning', text: 'Al someter, el sistema valida los topes. Si los excede verá "Excede el límite de horas regulares (diario y/o semanal)" con el nombre de cada trabajador afectado; corrija y vuelva a someter.' },
      ],
    },
    {
      id: 'guardar-enviar',
      group: 'Nómina',
      title: 'Guardar y someter la tarjeta',
      blocks: [
        { type: 'steps', items: [
          'Pulse "Guardar" para guardar la tarjeta como borrador y seguir trabajándola después.',
          'Cuando esté completa, pulse "Someter".',
          'La tarjeta se envía al Ingeniero(a) del proyecto para su revisión (Nivel 1) — tanto en proyectos de 2 como de 3 niveles.',
        ] },
        { type: 'note', variant: 'warning', text: 'Si pulsa "Someter" sin trabajadores con tarea y horas, aparece "Agrega al menos un empleado con tarea y horas antes de someter".' },
        { type: 'note', variant: 'info', text: 'Si sale con cambios sin guardar (botón "Volver"), el sistema le pregunta si desea Guardar como Borrador, Descartar la Tarjeta o Seguir Editando. Un borrador se puede eliminar con "Eliminar Borrador"; una vez sometida, ya no se puede eliminar.' },
      ],
    },
    {
      id: 'copiar-corregir',
      group: 'Nómina',
      title: 'Copiar una tarjeta y corregir una devuelta',
      blocks: [
        { type: 'subheading', text: 'Copiar una tarjeta' },
        { type: 'steps', items: [
          'En la lista de tarjetas del proyecto, pulse "Copiar" en la tarjeta que quiere duplicar.',
          'Elija la fecha nueva y confirme. Se copian los trabajadores y las tareas; las horas quedan en 0 para que las complete.',
        ] },
        { type: 'subheading', text: 'Corregir una tarjeta devuelta' },
        { type: 'steps', items: [
          'Una tarjeta "Devuelto" aparece en su proyecto; ábrala (es editable).',
          'Lea la "Razón de devolución" y las "Notas del revisor" en la parte superior.',
          'Corrija lo indicado y pulse "Reenviar".',
        ] },
        { type: 'note', variant: 'warning', text: 'Una tarjeta devuelta no se puede eliminar: solo se corrige y se reenvía.' },
      ],
    },
    {
      id: 'historial',
      group: 'Nómina',
      title: 'Consultar el estado de las tarjetas',
      blocks: [
        { type: 'steps', items: [
          'En Nómina, expanda un proyecto para ver sus tarjetas agrupadas por estado.',
          'Entre al proyecto y use el filtro de fechas para encontrar una tarjeta específica.',
          'Abra una tarjeta sometida (modo solo lectura) para ver su detalle y el avance de la aprobación.',
        ] },
        { type: 'note', variant: 'info', text: 'La mini-cadena de aprobación en cada tarjeta le dice en qué nivel está y, si fue devuelta, a quién.' },
      ],
    },
  ],
};
